import React, { useMemo } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Modal from '@/v2/components/common/Modal'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsReminders,
  useAddReminder,
  useUpdateReminder,
  useDeleteReminder,
} from '@/v2/hooks/settings/useSettingsReminders'
import { useSettingsAppointmentTypes } from '@/v2/hooks/settings/useSettingsAppointmentTypes'
import useFormModal from '@/v2/hooks/settings/useFormModal'
import CancellationNoticeSection from '@/v2/components/settings/cancellation/CancellationNoticeSection'
import {
  SettingsPage,
  SettingsEmptyState,
  PrimaryButton,
  CardGrid,
  Card,
  CardTitle,
  CheckboxGroup,
} from '@/v2/components/settings/shared'

const TemplateDelivery = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
`

const DeliveryIcon = styled.div`
  width: 1rem;
  height: 1rem;
  color: ${props => (props.active ? '#10b981' : '#dc2626')};
`

const AppointmentTypesSection = styled.div`
  margin-bottom: 1rem;
`

const SectionTitle = styled.h5`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const AppointmentTypesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const AppointmentTypeItem = styled.li`
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0.125rem 0;
`

// Lines up under a checkbox label.
const HelpText = styled.p`
  margin: 0 0 0.25rem 2rem;
  font-size: 0.8125rem;
  color: #6b7280;
`

const INITIAL_FORM = {
  periodBefore: 1,
  periodBeforeUnit: 'day',
  byEmail: true,
  bySms: false,
  emailMessage: '',
  smsMessage: '',
  skipIfIntakeCompleted: false,
  includeHealthHistoryLink: false,
  appointmentTypeIds: [],
}

const periodOptions = [
  { value: 'hour', label: 'Hours' },
  { value: 'day', label: 'Days' },
]

const Reminders = () => {
  const {
    reminders: rawReminders,
    isLoading: remindersLoading,
  } = useSettingsReminders()
  const {
    appointmentTypes: rawAppointmentTypes,
    isLoading: apptTypesLoading,
  } = useSettingsAppointmentTypes()
  const { addReminder, isPending: isAdding } = useAddReminder()
  const { updateReminder, isPending: isUpdating } = useUpdateReminder()
  const { deleteReminder, isPending: isDeleting } = useDeleteReminder()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    showModal,
    editingItem,
    formData,
    isEditing,
    openForCreate,
    openForEdit,
    close,
    updateField,
    setFormData,
  } = useFormModal(INITIAL_FORM)

  const loading = remindersLoading || apptTypesLoading
  const isSaving = isAdding || isUpdating

  const templates = useMemo(
    () =>
      rawReminders.map(t => ({
        id: t.id,
        periodBefore: t.periodBefore,
        periodBeforeUnit: t.periodBeforeUnit,
        byEmail: t.byEmail,
        bySms: t.bySMS,
        emailMessage: t.emailMessage || '',
        smsMessage: t.smsMessage || '',
        skipIfIntakeCompleted: !!t.skipIfIntakeCompleted,
        includeHealthHistoryLink: !!t.includeHealthHistoryLink,
        appointmentTypes: t.appointmentTypes || [],
      })),
    [rawReminders]
  )

  const appointmentTypes = useMemo(
    () => rawAppointmentTypes.map(at => ({ id: at.id, title: at.title })),
    [rawAppointmentTypes]
  )

  const getTemplateTitle = template => {
    const time =
      template.periodBefore === 1
        ? `${template.periodBefore} ${template.periodBeforeUnit}`
        : `${template.periodBefore} ${template.periodBeforeUnit}s`
    return `${time} before appointment`
  }

  const handleEdit = template => {
    openForEdit(template, t => ({
      periodBefore: t.periodBefore,
      periodBeforeUnit: t.periodBeforeUnit,
      byEmail: t.byEmail,
      bySms: t.bySms,
      emailMessage: t.emailMessage,
      smsMessage: t.smsMessage,
      skipIfIntakeCompleted: t.skipIfIntakeCompleted,
      includeHealthHistoryLink: t.includeHealthHistoryLink,
      appointmentTypeIds: t.appointmentTypes.map(at => at.id),
    }))
  }

  const handleDelete = async templateId => {
    if (confirm('Are you sure you want to delete this reminder template?')) {
      try {
        await deleteReminder(templateId)
        onOpenSnackbar('Reminder template deleted successfully', 'success')
        close()
      } catch (error) {
        onOpenSnackbar('Failed to delete reminder template', 'error')
      }
    }
  }

  const handleSubmit = async () => {
    const reminderData = {
      period_before: formData.periodBefore,
      period_before_unit: formData.periodBeforeUnit,
      by_email: formData.byEmail,
      by_sms: formData.bySms,
      email_message: formData.emailMessage,
      sms_message: formData.smsMessage,
      skip_if_intake_completed: formData.skipIfIntakeCompleted,
      include_health_history_link: formData.includeHealthHistoryLink,
      appointment_type_ids: formData.appointmentTypeIds,
    }

    try {
      if (isEditing) {
        await updateReminder({ id: editingItem.id, ...reminderData })
        onOpenSnackbar('Reminder template updated successfully', 'success')
      } else {
        await addReminder(reminderData)
        onOpenSnackbar('Reminder template created successfully', 'success')
      }
      close()
    } catch (error) {
      onOpenSnackbar('Failed to save reminder template', 'error')
    }
  }

  const handleAppointmentTypeToggle = appointmentTypeId => {
    setFormData(prev => ({
      ...prev,
      appointmentTypeIds: prev.appointmentTypeIds.includes(appointmentTypeId)
        ? prev.appointmentTypeIds.filter(id => id !== appointmentTypeId)
        : [...prev.appointmentTypeIds, appointmentTypeId],
    }))
  }

  return (
    <SettingsPage
      title="Reminders"
      subtitle="Configure automated appointment reminders and notifications"
      loading={loading}
      actions={
        <PrimaryButton onClick={openForCreate}>+ Add Template</PrimaryButton>
      }
    >
      {templates.length === 0 ? (
        <SettingsEmptyState
          icon={
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          }
          heading="No reminder templates yet"
          description="Create templates to automatically send appointment reminders to your patients"
          actionLabel="Add your first template"
          onAction={openForCreate}
        />
      ) : (
        <CardGrid $minWidth="320px">
          {templates.map(template => (
            <Card key={template.id} onClick={() => handleEdit(template)}>
              <CardTitle $mb="0.75rem">{getTemplateTitle(template)}</CardTitle>

              <TemplateDelivery>
                <DeliveryIcon active={template.byEmail || template.bySms}>
                  {template.byEmail || template.bySms ? (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  )}
                </DeliveryIcon>
                {template.byEmail && template.bySms
                  ? 'By Email & SMS'
                  : template.byEmail
                  ? 'By Email'
                  : template.bySms
                  ? 'By SMS'
                  : 'Inactive'}
              </TemplateDelivery>

              {template.skipIfIntakeCompleted && (
                <TemplateDelivery>
                  Skipped once the health history is completed
                </TemplateDelivery>
              )}

              {template.includeHealthHistoryLink && (
                <TemplateDelivery>
                  Adds the health history link until it's completed
                </TemplateDelivery>
              )}

              <AppointmentTypesSection>
                <SectionTitle>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
                  </svg>
                  For Appointment Types
                </SectionTitle>
                <AppointmentTypesList>
                  {template.appointmentTypes.map(appType => (
                    <AppointmentTypeItem key={appType.id}>
                      {appType.title}
                    </AppointmentTypeItem>
                  ))}
                </AppointmentTypesList>
              </AppointmentTypesSection>
            </Card>
          ))}
        </CardGrid>
      )}

      <CancellationNoticeSection />

      <Modal
        isOpen={showModal}
        title={isEditing ? 'Edit Reminder Template' : 'New Reminder Template'}
        onClose={close}
        onConfirm={handleSubmit}
        confirmLabel={isEditing ? 'Save Changes' : 'Create Template'}
        cancelLabel="Cancel"
        submitDisabled={isSaving}
        loading={isSaving}
        maxWidth="sm"
        fullWidth
        showExtraButton={isEditing}
        extraButtonLabel="Delete Template"
        extraButtonColor="error"
        onExtraButtonClick={() => handleDelete(editingItem?.id)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography bold fontSize="1rem">
              Timing
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                fullWidth
                type="number"
                label="Send reminder"
                value={String(formData.periodBefore)}
                onChange={val =>
                  updateField('periodBefore', parseInt(val) || 1)
                }
              />
              <SelectField
                fullWidth
                label="Time unit"
                options={periodOptions}
                value={formData.periodBeforeUnit}
                onChange={val => updateField('periodBeforeUnit', val)}
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Delivery Methods
            </Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}
            >
              <CheckboxField
                label="Send by Email"
                checked={formData.byEmail}
                onChange={e => updateField('byEmail', e.target.checked)}
              />

              <CheckboxField
                label="Send by SMS"
                checked={formData.bySms}
                onChange={e => updateField('bySms', e.target.checked)}
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Health History
            </Typography>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}
            >
              <CheckboxField
                label="Don't send if the health history is already completed"
                checked={formData.skipIfIntakeCompleted}
                onChange={e =>
                  updateField('skipIfIntakeCompleted', e.target.checked)
                }
              />
              <Box>
                <CheckboxField
                  label="Include health history link"
                  checked={formData.includeHealthHistoryLink}
                  onChange={e =>
                    updateField('includeHealthHistoryLink', e.target.checked)
                  }
                />
                <HelpText>
                  Adds a link to the patient's health history at the end of the
                  message, only while they haven't completed it. It isn't added
                  if the message already has the link.
                </HelpText>
                {formData.includeHealthHistoryLink && formData.bySms && (
                  <HelpText>
                    The link adds about 130 characters to the text message.
                  </HelpText>
                )}
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Appointment Types
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CheckboxGroup>
                {appointmentTypes.map(appointmentType => (
                  <CheckboxField
                    key={appointmentType.id}
                    label={appointmentType.title}
                    checked={formData.appointmentTypeIds.includes(
                      appointmentType.id
                    )}
                    onChange={() =>
                      handleAppointmentTypeToggle(appointmentType.id)
                    }
                  />
                ))}
              </CheckboxGroup>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="Email message"
                multiline
                rows={8}
                value={formData.emailMessage}
                onChange={val => updateField('emailMessage', val)}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="SMS message"
                multiline
                rows={8}
                value={formData.smsMessage}
                onChange={val => updateField('smsMessage', val)}
              />
            </Box>
          </Box>

          <Box>
            <Typography bold fontSize="1rem">
              Setting up messages
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography fontSize="0.875rem" style={{ color: '#6b7280' }}>
                Use Liquid templating syntax with {'{{...}}'} for dynamic
                content. Available variables:
              </Typography>
              <Box
                component="ul"
                sx={{
                  mt: 0.5,
                  pl: 2,
                  fontSize: '0.8125rem',
                  color: '#6b7280',
                  lineHeight: 1.8,
                }}
              >
                <li>
                  <code>patient.full_name</code>,{' '}
                  <code>patient.first_name</code>,{' '}
                  <code>patient.last_name</code>
                </li>
                <li>
                  <code>appointment.date</code>, <code>appointment.time</code>,{' '}
                  <code>appointment.weekday</code>
                </li>
                <li>
                  <code>appointment.type</code>,{' '}
                  <code>appointment.doctor_name</code>
                </li>
                <li>
                  <code>appointment.is_confirmed</code>,{' '}
                  <code>appointment.confirmation_url</code>
                </li>
                <li>
                  <code>intake_form.filled?</code>, <code>intake_form.url</code>
                </li>
              </Box>
              <Typography
                fontSize="0.8125rem"
                style={{ color: '#6b7280', marginTop: '0.5rem' }}
              >
                SMS text is limited to 130 characters, excepting Liquid syntax
                elements.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </SettingsPage>
  )
}

export default Reminders
